import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OverlayModule } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

import { RoutePaths } from '../../../shared/api/types/enums';
import { AppNotification, NotificationsService } from '../notifications.service';
import { TopbarMenuService } from '../topbar-menu.service';

/**
 * Notifications bell + panel (NBO-2331, live since NBO-2207).
 *
 * The panel renders whatever NotificationsService derives — accent badge
 * (never red) counting UNSEEN and capped at "9+", cleared when the panel
 * opens; rows keeping an unread brand-edge until individually read;
 * Today/Earlier grouping; the "Open Action Hub" footer; the "You're all
 * caught up" empty state. Every row and the footer navigate to the Action
 * Hub — the notification IS the discovery affordance the Action Hub lacked.
 */
@Component({
  selector: 'app-topbar-notifications',
  standalone: true,
  imports: [OverlayModule, TranslateModule, MatIconModule],
  templateUrl: './topbar-notifications.component.html',
  styleUrls: ['./topbar-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarNotificationsComponent {
  // Shared across the five clusters so only one popover is open and switching
  // between triggers takes one click (see TopbarMenuService).
  readonly open = this.menu.openFor('bell');

  readonly notifications = toSignal(this.notificationsService.list(), { initialValue: [] as AppNotification[] });

  /** Unseen clears when the panel opens (persisted); unread marks persist per row. */
  readonly unseenCount = computed(() => this.notifications().filter((n) => n.unseen).length);

  /** Badge caps at "9+" — a precise large number is noise, not information. */
  readonly badgeLabel = computed(() => (this.unseenCount() > 9 ? '9+' : String(this.unseenCount())));

  /** One-shot pulse when the unseen count grows while the badge is showing. */
  readonly bump = signal(false);
  private previousUnseen = 0;

  readonly todayRows = computed(() => this.notifications().filter((n) => n.group === 'today'));
  readonly earlierRows = computed(() => this.notifications().filter((n) => n.group === 'earlier'));
  readonly isEmpty = computed(() => this.notifications().length === 0);

  private readonly ICONS: Record<AppNotification['kind'], string> = {
    assigned: 'inbox',
    status: 'sync',
    import: 'upload',
    mention: 'person_add'
  };

  constructor(
    private menu: TopbarMenuService,
    private notificationsService: NotificationsService,
    private router: Router
  ) {
    effect(() => {
      const count = this.unseenCount();
      if (count > this.previousUnseen && this.previousUnseen > 0) {
        // Growth while already visible pulses; the initial appearance animates
        // via the CSS entry animation instead, so the two never stack.
        this.bump.set(true);
      }
      this.previousUnseen = count;
    });
  }

  toggle(): void {
    this.menu.toggle('bell');
    if (this.open()) {
      this.notificationsService.markAllSeen();
    }
  }

  close(): void {
    this.menu.close();
  }

  onBackdropClick(event: MouseEvent): void {
    this.menu.handleBackdropClick(event);
  }

  onDetach(): void {
    this.menu.closeIf('bell');
  }

  markAllRead(): void {
    this.notificationsService.markAllRead();
  }

  openRow(row: AppNotification): void {
    this.notificationsService.markRead(row.id);
    this.openActionHub();
  }

  openActionHub(): void {
    this.close();
    this.router.navigate(['/', RoutePaths.AiActionFeed]);
  }

  iconFor(kind: AppNotification['kind']): string {
    return this.ICONS[kind];
  }
}
